/*!
 * Copyright © 2025 Avtavivo
 */

import * as AV from "./Interfaces/AV.Interfaces";
import { Server } from "../Platform/Server";
import { AVAuth } from "../Platform/AVAuth";
import { AVEntity } from "./AVEntity";
import { Loc } from "../App/Loc";
import { SignalRService } from "./SignalR";

//@ts-ignore
/** Person API client: wraps /api/Person endpoints for self-loading (getSelf with etag caching), profile save, guardian/dependent management, FCM token registration, favorites, activities, contact verification, and account linking. Caches the current user in the static _self field and propagates updates via checkIfSelf(). */
export class Person extends AVEntity {
  static _self: AV.IPerson;
  static _api = "/api/Person";
  static _entity = "/api/Entity";

  public static async getbyName(path: string): Promise<AV.IPerson> {
    var p: AV.IPerson = await Server.get<AV.IPerson>(Person._api + path);
    return p;
  }

  public static self(): AV.IPerson {
    return this._self;
  }

  static checkIfSelf(val: AV.IAVEntity) {
    if (this._self && val?.id === this._self.id) this._self = val;
  }

  public static async getSelf(force: boolean = false): Promise<AV.IPerson> {
    if (!Person._self || force) {
      //Dont use Signal because message is big
      //if (SignalRService.isActive())
      //	Person._self = await SignalRService.getSelf();
      //else
      var api = Person._api + "/GetSelf";

      const etag = Person._self ? Person._self._etag : null;
      var retval = await Server.get<AV.IPerson>(api, { etag });
      if (retval) Person._self = retval;

      if (!Person._self)
      	  AVAuth.logout();
      Loc.SetLang(Person._self?.preferences?.lang);
    }
    return Person._self;
  }

  // FIXES TS2417: Matches base class signature exactly by removing the 'extends' constraint here
  public static override async get<T>(fk?: AV.IFK): Promise<T> {
    if (fk && Object.keys(fk).length > 0) {
      var p: T = await AVEntity.get<T>(fk);
      return p;
    }
    
    // Double cast handles the unconstrained T safely for the fallback path
    const self = await Person.getSelf();
    return self as unknown as T;
  }

  public static async getPerson(person?: AV.IPerson): Promise<AV.IPerson> {
    if (person && Object.keys(person).length > 0) {
      //if (SignalRService.isActive())
      //	return await SignalRService.invoke<AV.IPerson, AV.IFK>("GetEntity", fk);
      //else {

      var p: AV.IPerson = await AVEntity.getEntity(person);
      return p;
      //	}
    }
    return await Person.getSelf();
  }

  public static async activate(req: AV.ITokenRequest): Promise<AV.IPerson> {
    var p: AV.IPerson = await Server.post<AV.IPerson>(
      Person._api + "/Activate",
      req,
    );
    if (p) this._self = p;
    return p;
  }

  public static async checkActivate(req: AV.ITokenRequest): Promise<boolean> {
    return await Server.post<boolean>(Person._api + "/CheckActivate", req);
  }

  public static makeSmall(val: AV.IAVEntity): AV.IAVEntity {
    let newval: AV.IAVEntity = JSON.parse(JSON.stringify(val));
    newval.autoFollows = undefined;
    newval.follows = undefined;
    newval.convos = undefined;
    newval.myRequests = undefined;
    newval.albums = undefined;
    newval.files = undefined;
    newval.members = undefined;
    newval.orgs = undefined;
    newval.ownedEvents = undefined;

    //Person
    (<AV.IPerson>newval).events = undefined;
    (<AV.IPerson>newval).wards = undefined;
    (<AV.IPerson>newval).guardians = undefined;
    (<AV.IPerson>newval).requests = undefined;
    (<AV.IPerson>newval).blockedBy = undefined;
    (<AV.IPerson>newval).fcmTokens = undefined;
    (<AV.IPerson>newval).blocked = undefined;
    (<AV.IPerson>newval).favs = undefined;
    (<AV.IPerson>newval).reported = undefined;
    (<AV.IPerson>newval).silenced = undefined;
    (<AV.IPerson>newval).autoOrg = undefined;
    (<AV.IPerson>newval).fcm = undefined;
    (<AV.IPerson>newval).plan = undefined;

    return newval;
  }

  // FIXES TS2417 for save(): Matches base class generic signature exactly
  public static override async save<T extends AV.IAVEntity>(val: T): Promise<T> {
    // 1. Cast input to IPerson to safely use makeSmall
    const smallVal = this.makeSmall(val as unknown as AV.IPerson);

    // 2. Post to the server keeping the generic return type T
    var p: T = await Server.post<T>(Person._entity, smallVal);
    
    // 3. Cast the result to IPerson internally for your specific self-check logic
    const personResult = p as unknown as AV.IPerson;
    if (personResult.id === this._self?.id) {
      this._self = personResult;
    }
    
    return p;
  }

  public static async getNoEmailInviteURL(): Promise<string> {
    return await Server.get(this._api + "/GetInviteNoEmailURL");
  }

  public static async getAccounts(): Promise<AV.ILoginAccount[]> {
    return await Server.get(this._api + "/GetAccounts");
  }

  //Add Dependent, remove  is a request
  public static async removeDependent(child: AV.ILink): Promise<AV.IPerson> {
    return await Server.post(this._api + "/RemoveDependent", child);
  }

  public static async addGuardian(parent: AV.IContact): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AddGuardian", parent);
  }

  public static async addDependent(parent: AV.IContact): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AddDependent", parent);
  }

  public static async removeGuardian(parent: AV.IFK): Promise<AV.IPerson> {
    return await Server.post(this._api + "/RemoveGuardian", parent);
  }

  public static async createInvite(child: AV.IContact[]): Promise<AV.IPerson> {
    return await Server.post(this._api + "/CreateInvite", child);
  }

  public static async inviteTeam(contact: AV.IContact): Promise<AV.IPerson> {
    return await Server.post(this._api + "/InviteTeam", contact);
  }

  public static async acceptInviteTeam(param: AV.ILinkParam): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AcceptInviteTeam", param);
  }

  public static async changeContact(
    contact: AV.ITokenProcessRequest,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/ChangeContact", contact);
  }

  public static async changeAccount(
    contact: AV.ITokenProcessRequest,
  ): Promise<AV.ILoginAccount[]> {
    return await Server.post(this._api + "/ChangeAccount", contact);
  }

  public static async VerifyContact(
    contact: AV.ITokenProcessRequest,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/VerifyContact", contact);
  }

  public static async addFCMToken(token: string): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AddFCMToken", token);
  }

  public static async clientError(error: string) {
    return await Server.post(this._api + "/ClientError", error);
  }

  public static async removeFCMToken(token: string): Promise<AV.IPerson> {
    return await Server.post(this._api + "/RemoveFCMToken", token);
  }

  // Change the name so it does not conflict with AVEntity.addFavorite
  public static async addPersonFavorite(
    source: AV.IFK,
    target: AV.ILink,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AddFavorite", {
      source: source,
      target: target,
    });
  }

  // Change the name so it doesn't conflict with AVEntity.removeFavorite
  public static async removePersonFavorite(
    source: AV.IFK,
    target: AV.ILink,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/RemoveFavorite", {
      source: source,
      target: target,
    });
  }

  public static async addActivity(
    source: AV.IFK,
    target: AV.IActivity,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/AddActivity", {
      source: source,
      target: target,
    });
  }

  public static async removeActivity(
    source: AV.IFK,
    target: AV.IActivity,
  ): Promise<AV.IPerson> {
    return await Server.post(this._api + "/RemoveActivity", {
      source: source,
      target: target,
    });
  }
}
